/**
 * Universal Size Selector Custom Element
 *
 * Provides functionality for switching between different size formats (US, UK, EU)
 * and updating the variant selector display accordingly.
 * Integrates with the variant selector to handle size selection and form updates.
 */

declare global {
  interface Window {
    $universalSizeMap: Record<string, {
      SIZE_UK: string;
      SIZE_US: string;
      SIZE_EU: string;
      SIZE_CM: string;
      AVAILABLE: boolean;
    }>;
  }
}

export class UniversalSizeSelector extends HTMLElement {
  private selectedSizeType: string = 'EU'
  private sizeTypeButton: HTMLElement | null = null
  private sizeTypeLabel: HTMLElement | null = null
  private sizeTypeOptions: HTMLElement | null = null
  private sizeMetricInput: HTMLInputElement | null = null
  private productId: string = ''

  connectedCallback(): void {
    this.init()
    this.attachEventListeners()
    this.updateSizeDisplay()
  }

  private init(): void {
    this.sizeTypeButton = this.querySelector('[data-size-type-button]')
    this.sizeTypeLabel = this.querySelector('[data-size-type-label]')
    this.sizeTypeOptions = this.querySelector('[data-size-type-options]')
    this.sizeMetricInput = this.querySelector('[data-size-metric-input]')

    this.selectedSizeType = this.dataset.selectedSize || 'EU'
    this.productId = this.dataset.productId || ''

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = this.selectedSizeType
    }
  }

  private attachEventListeners(): void {
    if (this.sizeTypeOptions) {
      this.sizeTypeOptions.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest('[data-size-type]') as HTMLElement
        if (button) {
          const newSizeType = button.dataset.sizeType
          if (newSizeType) {
            this.changeSizeType(newSizeType)
          }
        }
      })
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (variantGridContainer) {
      variantGridContainer.addEventListener('click', (e) => {
        const variantButton = (e.target as HTMLElement).closest('[data-variant-id]') as HTMLElement
        if (variantButton && variantButton.dataset.selectOption === 'true') {
          this.updateVariantActiveState(variantButton)
          this.updateSizeMetric()
        }
      })
    }
  }

  private changeSizeType(newSizeType: string): void {
    this.selectedSizeType = newSizeType

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = newSizeType
    }

    this.updateSizeTypeActiveState()
    this.updateSizeDisplay()
    this.updateSizeMetric()
  }

  private updateSizeTypeActiveState(): void {
    if (!this.sizeTypeOptions) return

    const buttons = this.sizeTypeOptions.querySelectorAll('[data-size-type]')
    buttons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const isActive = htmlButton.dataset.sizeType === this.selectedSizeType

      htmlButton.classList.toggle('font-bold', isActive)
      htmlButton.classList.toggle('bg-primary', isActive)
      htmlButton.classList.toggle('text-secondary', isActive)
    })
  }

  private updateSizeDisplay(): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer || !window.$universalSizeMap) return

    const variantButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    variantButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const variantId = htmlButton.dataset.variantId

      if (variantId) {
        const variantData = window.$universalSizeMap[variantId]

        if (variantData) {
          let displaySize = ''

          switch (this.selectedSizeType) {
            case 'UK':
              displaySize = variantData.SIZE_UK
              break
            case 'EU':
              displaySize = variantData.SIZE_EU
              break
            case 'US':
            default:
              displaySize = variantData.SIZE_US
              break
          }

          htmlButton.style.setProperty('--display-size', `"${displaySize}"`)
        }
      }
    })
  }

  private updateVariantActiveState(selectedButton: HTMLElement): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer) return

    const allButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    allButtons.forEach((button) => {
      button.classList.remove('active')
    })

    selectedButton.classList.add('active')
  }

  private updateSizeMetric(): void {
    if (!this.sizeMetricInput) return

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    if (!activeVariantButton) return

    const variantId = activeVariantButton.dataset.variantId
    if (!variantId) return

    const variantData = window.$universalSizeMap?.[variantId]
    if (!variantData) return

    let sizeValue = ''

    switch (this.selectedSizeType) {
      case 'UK':
        sizeValue = `UK ${variantData.SIZE_UK}`
        break
      case 'US':
        sizeValue = `US ${variantData.SIZE_US}`
        break
      case 'EU':
      default:
        sizeValue = `EU ${variantData.SIZE_EU}`
        break
    }

    this.sizeMetricInput.value = sizeValue
  }

  /**
   * Public method to get current size type
   */
  public getSizeType(): string {
    return this.selectedSizeType
  }

  /**
   * Public method to set size type programmatically
   */
  public setSizeType(sizeType: string): void {
    if (['EU', 'UK', 'US'].includes(sizeType)) {
      this.changeSizeType(sizeType)
    }
  }

  /**
   * Public method to get selected variant ID
   */
  public getSelectedVariantId(): string | null {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    return activeVariantButton?.dataset.variantId || null
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const globalDefaultSize = (window as any).product_default_size
  if (globalDefaultSize) {
    const selectors = document.querySelectorAll('universal-size-selector')
    selectors.forEach((selector) => {
      const element = selector as UniversalSizeSelector
      element.setSizeType(globalDefaultSize)
    })
  }
})

export default UniversalSizeSelector
